import React, { useRef, useState, useEffect } from 'react';
import './App.css';
import MyDoodles from './MyDoodles';
import Gallery from './Gallery';
import { GuidProvider, useGuid } from './GuidContext';
import { useDevice } from './useDevice';
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import HelpModal from './HelpModal.js'
import squiggleLine from './squiggleLine.png'
import straightLine from './straightLine.png'
import undoIcon from './undoIcon.png'
import sampleIcon from './sampleIcon.png'
import blendingIcon from './blending.png'
import transformIcon from './transform.png';
import TimelapseExportButton from './timelapse/ExportButton';
import { purgeSession, purgeOlderThan, getTodayCST } from './timelapse/db';
import { resetCaptureState } from './timelapse/capture';
import CanvasNav from './CanvasNav';
import SiteNav from './desktop/SiteNav';
import Root from './Root';
import BlogList from './blog/BlogList';
import BlogPost from './blog/BlogPost';
import MobileColorPicker from './MobileColorPicker';

const DEFAULT_PAGE_BG = '#ececec'; // must match the body rule in App.css


function TimelapseFab({ isTablet, guid }) {
  const location = useLocation();
  if (!isTablet) return null;
  const queryForce = new URLSearchParams(location.search).get('canvas') === '1';
  const onCanvas = location.pathname === '/' && queryForce;
  if (!onCanvas) return null;
  return (
    <div style={timelapseFabStyle}>
      <TimelapseExportButton guid={guid} />
    </div>
  );
}

function capitalizeEachWord(str) {
  return str.replace(/\b\w/g, (match) => match.toUpperCase());
}

function AppHeader({
  isDesktop,
  isTablet,
  isPublished,
  onPublish,
  onShare,
  onOpenHelp,
  showSubject,
  subjectText,
}) {
  const location = useLocation();
  const path = location.pathname;
  const isBlog = path === '/blog' || path.startsWith('/blog/');
  const queryForce = new URLSearchParams(location.search).get('canvas') === '1';
  const onWelcome = (isDesktop || isTablet) && path === '/' && !queryForce;

  if (onWelcome || isBlog) {
    return <SiteNav />;
  }
  return (
    <>
      <CanvasNav
        isPublished={isPublished}
        onPublish={onPublish}
        onShare={onShare}
        onOpenHelp={onOpenHelp}
      />
      {showSubject && <div style={subjectStyle}>{subjectText}</div>}
    </>
  );
}

function App() {


  return (

    <GuidProvider>
      <GuidComponent />
    </GuidProvider>

  );
}


function GuidComponent() {
  const [isPublished, setIsPublished] = useState(() => {
    // Initially set isPublished based on local storage to retain state across refreshes
    return localStorage.getItem('isPublished') === 'true';
  }); const saveDrawingRef = useRef(null);
  const { guid } = useGuid();
  const [dailySubject, setDailySubject] = useState('');
  const [isLoading, setIsLoading] = useState(true); // New state for loading
  const [color, setColor] = useState('#000000')
  const shareDrawingRef = useRef(null);
  const { isPhone, isTablet, isDesktop } = useDevice();
  const [showSubject, setShowSubject] = useState(true);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [handedness, setHandedness] = useState(() => {
    const saved = localStorage.getItem('handedness');
    return saved === 'left' || saved === 'right' ? saved : 'right';
  });

  useEffect(() => {
    localStorage.setItem('handedness', handedness);
  }, [handedness]);

  // Page background around the canvas — a viewing preference (dark drawings
  // are hard to judge against a light page), persisted across days.
  const [pageBgColor, setPageBgColor] = useState(
    () => localStorage.getItem('pageBgColor') || DEFAULT_PAGE_BG);
  const [pageBgPickerOpen, setPageBgPickerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('pageBgColor', pageBgColor);
  }, [pageBgColor]);

  useEffect(() => {
    document.body.style.background = pageBgColor;
  }, [pageBgColor]);

  // Callback function to handle publish event
  const onPublish = () => {
    if (saveDrawingRef.current && typeof saveDrawingRef.current === 'function') {
      saveDrawingRef.current();
      setIsPublished(true); // Assuming you might set this upon successful publication
      localStorage.setItem('isPublished', 'true'); // Save to local storage
      if (isTablet && guid) {
        purgeSession(guid, getTodayCST()).catch(() => {});
        resetCaptureState();
      }
    }
  };

  // One-shot cleanup of timelapse frames older than 7 days
  useEffect(() => {
    if (!isTablet) return;
    const cb = () => purgeOlderThan(7).catch(() => {});
    if (window.requestIdleCallback) window.requestIdleCallback(cb, { timeout: 5000 });
    else setTimeout(cb, 2000);
  }, [isTablet]);


  // Watch for changes in isPublished to update local storage
  useEffect(() => {
    localStorage.setItem('isPublished', isPublished.toString());
  }, [isPublished]);

  const onShare = () => {
    if (shareDrawingRef.current) {
      shareDrawingRef.current(); // This will call the shareDrawing function in DrawingCanvas
    }
  };

  useEffect(() => {

    const fetchDailySubject = async () => {
      try {
        setIsLoading(true); // Set loading to true
        const response = await fetch('https://ibt18xjvyj.execute-api.us-east-1.amazonaws.com/getDailySubject');
        const data = await response.json();
        setDailySubject(capitalizeEachWord(data.subject));
      } catch (error) {
        console.error("Failed to fetch daily subject:", error);
      } finally {
        setIsLoading(false); // Set loading to false
      }
    };

    fetchDailySubject();
  }, []);


  const deviceClass = isPhone ? 'phone' : isTablet ? 'tablet' : 'desktop';

  const canvasProps = {
    color,
    setColor,
    mySaveDrawingRef: saveDrawingRef,
    isPublished,
    setIsPublished,
    myShareDrawingRef: shareDrawingRef,
    setShowSubject,
    handedness,
  };

  return (
    <Router>
      <div data-device-class={deviceClass}>
        <AppHeader
          isDesktop={isDesktop}
          isTablet={isTablet}
          isPublished={isPublished}
          onPublish={onPublish}
          onShare={onShare}
          onOpenHelp={() => setIsHelpModalOpen(true)}
          showSubject={showSubject}
          subjectText={isLoading ? 'Loading...' : `Today's Subject: ${dailySubject}`}
        />

        <Routes>
          <Route path="/" element={<Root canvasProps={canvasProps} subject={dailySubject} />} />
          <Route path="/Gallery" element={<Gallery setShowSubject={setShowSubject} />} />
          <Route path="/my-doodles" element={<MyDoodles setShowSubject={setShowSubject} />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
        </Routes>

        <HelpModal open={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)}>
          <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <img src={undoIcon} alt="Undo" style={{ width: '20px' }} /> <strong>Undo</strong> — Revert last action
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <img src={sampleIcon} alt="Eyedropper" style={{ width: '20px' }} /> <strong>Eyedropper</strong> — Pick a color from the canvas
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <img src={blendingIcon} alt="Blend" style={{ width: '20px' }} /> <strong>Blend</strong> — Smooth and mix colors
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <img src={straightLine} alt="Line modes" style={{ width: '20px' }} />
              <img src={squiggleLine} alt="Line modes" style={{ width: '20px' }} /> <strong>Line Mode</strong> — Straight or freehand
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <img src={transformIcon} alt="Transform" style={{ width: '20px' }} /> <strong>Transform</strong> — Two-finger move and scale
            </div>
            <div style={{ marginBottom: '6px' }}>
              <strong>Alpha</strong> — Opacity: 10%, 50%, or 100%
            </div>
            {isTablet && (
              <>
                <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #ccc' }} />
                <div style={{ marginBottom: '6px' }}>
                  <strong>Handedness</strong> — Position of the on-canvas hold buttons
                </div>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                  <button
                    type="button"
                    onClick={() => setHandedness('left')}
                    style={handednessOptionStyle(handedness === 'left')}
                  >
                    Left-handed
                  </button>
                  <button
                    type="button"
                    onClick={() => setHandedness('right')}
                    style={handednessOptionStyle(handedness === 'right')}
                  >
                    Right-handed
                  </button>
                </div>
              </>
            )}
            <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #ccc' }} />
            <div style={{ marginBottom: '6px' }}>
              <strong>Page background</strong> — Color of the page around the canvas
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <button
                type="button"
                aria-label="Page background color"
                onClick={() => setPageBgPickerOpen(true)}
                style={{
                  width: '35px',
                  height: '35px',
                  padding: 0,
                  backgroundColor: pageBgColor,
                  border: '1px solid black',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                }}
              />
              <button
                type="button"
                onClick={() => setPageBgColor(DEFAULT_PAGE_BG)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ccc',
                  background: '#f5f5f5',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              >
                Reset
              </button>
            </div>
            <hr style={{ margin: '10px 0', border: 'none', borderTop: '1px solid #ccc' }} />
            <div style={{ textAlign: 'center' }}>
              <a href="mailto:ittygames2@gmail.com?subject=The Daily Art Feedback" style={{ color: '#4CAF50' }}>
                Send Feedback
              </a>
            </div>
          </div>
        </HelpModal>
        <MobileColorPicker
          open={pageBgPickerOpen}
          color={pageBgColor}
          onChange={setPageBgColor}
          onClose={() => setPageBgPickerOpen(false)}
          title="Page Background"
        />
        <TimelapseFab isTablet={isTablet} guid={guid} />
      </div>
    </Router>

  );
}

const timelapseFabStyle = {
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1000,
  pointerEvents: 'none',
};

// Style for the daily subject
const subjectStyle = {
  textAlign: 'center',
  marginTop: '10px',
  fontSize: '18px'
};

const handednessOptionStyle = (selected) => ({
  flex: 1,
  padding: '8px 12px',
  fontSize: '14px',
  fontWeight: selected ? 700 : 500,
  borderRadius: '6px',
  border: selected ? '2px solid #4CAF50' : '1px solid #bbb',
  background: selected ? '#E8F5E9' : '#fff',
  color: '#222',
  cursor: 'pointer',
});

export default App;